import type { RefCallback } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { imageLoadScheduler } from "./image-load-scheduler";

type ImageLoadingStatusType = "idle" | "loading" | "loaded" | "error";

const IMAGE_STATUS_LOADED: ImageLoadingStatusType = "loaded";
const IMAGE_STATUS_ERROR: ImageLoadingStatusType = "error";
const OBSERVER_ROOT_MARGIN = "200px";
// Must exceed PlantaAvatarImage's retry window (currently 1 retry × 500ms)
const SLOT_TIMEOUT_MS = 15_000;

class SharedVisibilityObserver {
  private observer: IntersectionObserver | null = null;
  private readonly callbacks = new WeakMap<Element, () => void>();

  observe(element: Element, onIntersect: () => void): void {
    this.callbacks.set(element, onIntersect);
    this.getOrCreateObserver().observe(element);
  }

  unobserve(element: Element): void {
    this.callbacks.delete(element);
    this.observer?.unobserve(element);
  }

  private getOrCreateObserver(): IntersectionObserver {
    this.observer ??= new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const callback = this.callbacks.get(entry.target);
            if (callback !== undefined) {
              callback();
              this.unobserve(entry.target);
            }
          }
        }
      },
      { rootMargin: OBSERVER_ROOT_MARGIN },
    );
    return this.observer;
  }
}

const sharedObserver = new SharedVisibilityObserver();

let nextId = 0;

export function useScheduledImageSrc(src: string | null): {
  readonly ref: RefCallback<Element>;
  readonly scheduledSrc: string | null;
  readonly onLoadingStatusChange: (status: ImageLoadingStatusType) => void;
} {
  const [id] = useState(() => `img-${String(nextId++)}`);
  const [canLoad, setCanLoad] = useState(false);
  const nodeRef = useRef<Element | null>(null);
  const releasedRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const releaseSlot = useCallback(() => {
    if (releasedRef.current) {
      return;
    }
    releasedRef.current = true;
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    imageLoadScheduler.release(id);
  }, [id]);

  useEffect(() => {
    if (src === null) {
      return;
    }

    releasedRef.current = false;

    imageLoadScheduler.enqueue(id, () => {
      setCanLoad(true);

      timeoutRef.current = setTimeout(() => {
        if (!releasedRef.current) {
          console.warn(
            JSON.stringify({
              message:
                "[useScheduledImageSrc] Slot timeout — image load did not complete",
              id,
            }),
          );
          releaseSlot();
        }
      }, SLOT_TIMEOUT_MS);
    });

    return (): void => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      imageLoadScheduler.cancel(id);
      setCanLoad(false);
    };
  }, [id, src, releaseSlot]);

  const onLoadingStatusChange = useCallback(
    (status: ImageLoadingStatusType) => {
      if (status !== IMAGE_STATUS_LOADED && status !== IMAGE_STATUS_ERROR) {
        return;
      }
      releaseSlot();
    },
    [releaseSlot],
  );

  const ref = useCallback(
    (node: Element | null) => {
      if (nodeRef.current !== null) {
        sharedObserver.unobserve(nodeRef.current);
        nodeRef.current = null;
      }

      if (node === null) {
        return;
      }

      if (typeof IntersectionObserver === "undefined") {
        return;
      }

      nodeRef.current = node;
      sharedObserver.observe(node, () => {
        imageLoadScheduler.promote(id);
      });
    },
    [id],
  );

  return {
    ref,
    scheduledSrc: canLoad ? src : null,
    onLoadingStatusChange,
  };
}
